import React, { createContext, useState, useEffect, useRef } from "react";
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from "primereact/toast";
import { useMsal } from "@azure/msal-react";
import useAxiosInstance from './auth/TokenAxiosHandler.jsx';

const UserContext = createContext();
const joinUrl = (base, path) => {
	const b = (base || '').replace(/\/+$/, '');
	const p = (path || '').replace(/^\/+/, '');
	return `${b}/${p}`;
};
const baseUrl = import.meta.env.VITE_FINDME_API;
const authUrl = import.meta.env.VITE_AUTH_API;
const authKey = import.meta.env.VITE_AUTH_KEY;
const baseKey = import.meta.env.VITE_FINDME_KEY || authKey;

const UserContextProvider = ({ children }) => {
	const [userInfo, setUserInfo] = useState(null);
	const [loadingError, setLoadingError] = useState(null);
	const [loading, setLoading] = useState(true);
	const { instance } = useMsal();
	const activeAccount = instance.getActiveAccount();
	const axiosInstance = useAxiosInstance();
	const toast = useRef(null);

	useEffect(() => {
		const fetchData = async () => {
			const accountEmail = activeAccount?.username || null;
			if (!accountEmail) {
				setLoadingError("No signed-in account found.");
				setLoading(false);
				return;
			}

			try {
				const userInfoResponse = await axiosInstance.get(
					`${joinUrl(authUrl, 'UserInfo')}?user=${encodeURIComponent(accountEmail)}`,
					{
						headers: {
							"ocp-apim-subscription-key": authKey,
						},
					}
				);
				userInfoResponse.data.email = accountEmail;
				setUserInfo(userInfoResponse.data);
				setLoadingError(null);
			} catch (error) {
				console.error("Failed to load user info:", error);
				setLoadingError("Failed to load user profile. Please refresh and try again.");
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	const apiCall = async (uri, body, operation) => {
		const headers = {
			"ocp-apim-subscription-key": baseKey,
		};
		const url = joinUrl(baseUrl, uri);

		switch (operation?.toUpperCase()) {
			case 'PUT':
				return axiosInstance.put(url, body, { headers });
			case 'POST':
				return axiosInstance.post(url, body, { headers });
			case 'DELETE':
				return axiosInstance.delete(url, { headers });
			case 'GET':
			default:
				return axiosInstance.get(url, {
					headers,
					...(body?.params ? { params: body.params } : {}),
				});
		}
	};

	const showSuccessToast = (msg, sticky = false) => {
		toast.current?.show({
			severity: "success",
			summary: "Success",
			detail: msg,
			...(sticky ? { sticky: true } : { life: 4000 }),
		});
	};

	const showErrorToast = (msg, sticky = false) => {
		toast.current?.show({
			severity: "error",
			summary: "Error",
			detail: msg,
			...(sticky ? { sticky: true } : { life: 4000 }),
		});
	};

	if (loadingError) {
		return (
			<UserContext.Provider value={{ userInfo }}>
				<div style={{ textAlign: 'center', padding: '40px' }}>{loadingError}</div>
			</UserContext.Provider>
		);
	}

	if (loading) {
		return (
			<UserContext.Provider value={{ userInfo }}>
				<div style={{ textAlign: 'center' }}><ProgressSpinner /></div>
			</UserContext.Provider>
		);
	}

	return (
		<UserContext.Provider value={{ userInfo, apiCall, showSuccessToast, showErrorToast }}>
			<Toast ref={toast} />
			{children}
		</UserContext.Provider>
	);
};

export { UserContext, UserContextProvider };
